import type { FC } from "react";

import { usePageMetadata } from "../../hooks/usePageMetadata";
import {
  PublicBillingFaqSection,
  PublicBillingHeroSection,
  PublicBillingPricingSection,
  PublicBillingRegisterSection,
  PublicBillingTransparencySection,
} from "./PublicBillingSections";
import { PublicPageShell } from "./PublicPageShell";

interface PublicBillingPageProps {
  onRequestOtp: (email: string) => Promise<void>;
  onVerifyOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
}

const BILLING_NAV_ITEMS = [
  { href: "/", label: "Overview" },
  { href: "/#models", label: "Models" },
  { href: "/#skill-mesh", label: "Skill Mesh" },
  { href: "#pricing", label: "Plans" },
  { href: "#faq", label: "FAQ" },
] as const;

export const PublicBillingPage: FC<PublicBillingPageProps> = ({
  onRequestOtp,
  onVerifyOtp,
}) => {
  usePageMetadata({
    description:
      "Arti pricing: one flat monthly price per Jira project with all AI compute included. No per-seat fees or long-term contracts.",
    keywords:
      "AI platform pricing, engineering capacity pricing, AI development cost, team plan pricing, enterprise AI pricing, no per-seat pricing",
    robots:
      "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      description:
        "Arti pricing: simple plans for engineering teams. No per-seat fees, no long-term contracts.",
      mainEntity: {
        "@type": "SoftwareApplication",
        applicationCategory: "BusinessApplication",
        featureList: [
          "Team and Enterprise plans",
          "No per-seat pricing",
          "All AI compute costs included",
          "No long-term contracts",
          "Full usage visibility",
        ],
        name: "Arti Pricing",
        operatingSystem: "Web",
      },
      name: "Arti Pricing",
      url: "/billing",
    },
    title: "Arti Pricing | Pay for Capacity, Not Seats",
    type: "website",
  });

  return (
    <PublicPageShell
      navItems={BILLING_NAV_ITEMS}
      primaryActionHref="#register"
      primaryActionLabel="Request access"
    >
      <PublicBillingHeroSection />
      <PublicBillingPricingSection />
      <PublicBillingTransparencySection />
      <PublicBillingFaqSection />
      <PublicBillingRegisterSection
        onRequestOtp={onRequestOtp}
        onVerifyOtp={onVerifyOtp}
      />
    </PublicPageShell>
  );
};
